import React, { useRef, useState, useEffect } from "react";
import emailjs from "emailjs-com";
import ReCAPTCHA from "react-google-recaptcha";
import { FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css"; // Import styles for phone input

const ContactSection = () => {
  const form = useRef();
  const [messageSent, setMessageSent] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [error, setError] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+44"); // Default to UK
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setShowCaptcha(true);
  }, []);

  // Handle reCAPTCHA
  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    setError(""); // Clear error when reCAPTCHA is completed
  };

  // Validate Email Format
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle Form Submission
  const sendEmail = (e) => {
    e.preventDefault();

    if (!name || !email || !phone || !message) {
      setError("⚠️ Please fill out all required fields.");
      return;
    }

    if (!validateEmail(email)) {
      setError("⚠️ Please enter a valid email address.");
      return;
    }

    if (!captchaValue) {
      setError("⚠️ Please complete the reCAPTCHA verification.");
      return;
    }

    emailjs
      .sendForm(
        "service_82itqtl", // Your EmailJS Service ID
        "template_1yhx7to", // Your EmailJS Template ID
        form.current,
        "1DRHqUq7hm1aNUCIm" // Your EmailJS Public Key
      )
      .then(
        (result) => {
          console.log(result.text);
          setMessageSent(true);
          form.current.reset();
          setEmail("");
          setPhone("+44"); // Reset to UK
          setName("");
          setMessage("");
          setCaptchaValue(null);
        },
        (error) => {
          console.log(error.text);
          setError("❌ Message failed to send. Please try again.");
        }
      );
  };

  return (
    <section id="contact" className="bg-gray-900 text-white py-20">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Title */}
        <div className="border-t-2 border-[#FFFFF] w-24 mx-auto my-12"></div>
        <h2 className="text-4xl font-bold text-center text-[#1C9BF8] mb-6">
          Get in Touch
        </h2>
        <p className="text-center text-gray-400 mb-10">
          Have questions? Fill out the form below, and we’ll get back to you soon.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Contact Form */}
          <div className="p-6 bg-gray-900 rounded-lg shadow-lg">
            <form ref={form} onSubmit={sendEmail} className="space-y-6">
              
              {/* Name Field */}
              <div>
                <label className="block text-gray-400">Name*</label>
                <input
                  type="text"
                  name="user_name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full p-3 mt-1 bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-[#1C9BF8]"
                  placeholder="Enter your name"
                />
              </div>

              {/* Phone Field with Country Code Selector */}
              <div>
                <label className="block text-gray-400">Phone*</label>
                <PhoneInput
                  country={"gb"} // Default to UK
                  value={phone}
                  onChange={(value) => setPhone(value)}
                  inputClass="custom-phone-input w-full h-12 px-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:ring-2 focus:ring-[#1C9BF8] shadow-sm"
                  containerClass="w-full" /* Ensures it takes full width */
                  buttonClass="bg-gray-700 border-gray-600 hover:bg-gray-600"
                  dropdownClass="bg-gray-900 text-white border-gray-700"
                  searchClass="bg-gray-800 text-white border-gray-600"
                />
                {/* Hidden Input for EmailJS */}
                <input type="hidden" name="user_phone" value={phone} />
              </div>

              {/* Email Field with Validation */}
              <div>
                <label className="block text-gray-400">Email*</label>
                <input
                  type="email"
                  name="user_email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full p-3 mt-1 bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-[#1C9BF8]"
                  placeholder="Enter your email"
                />
              </div>

              {/* Message Field */}
              <div>
                <label className="block text-gray-400">Message*</label>
                <textarea
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows="4"
                  className="w-full p-3 mt-1 bg-gray-800 rounded-lg border border-gray-700 focus:ring-2 focus:ring-[#1C9BF8]"
                  placeholder="Your message"
                ></textarea>
              </div>

              {/* reCAPTCHA - Always Visible */}
              {showCaptcha && (
                <div className="flex justify-center">
                  <ReCAPTCHA
                    sitekey="6Lc08dEqAAAAAK7d5t1ZSxNHuD08FlbqxUhJxsC7"
                    onChange={handleCaptchaChange}
                  />
                </div>
              )}

              {error && <p className="text-red-500 text-center">{error}</p>}
              {messageSent && (
                <p className="text-green-400 text-center mt-4">
                  ✅ Message sent successfully!
                </p>
              )}

              <button
                type="submit"
                className="w-full bg-[#1C9BF8] hover:bg-[#0D47A1] text-white font-bold py-3 rounded-lg transition-all duration-300"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Contact Info & Google Map */}
          <div className="p-6 bg-gray-900 rounded-lg shadow-lg">
            <h3 className="text-2xl font-semibold text-[#1C9BF8] mb-4">
              Contact Details
            </h3>
            <p className="text-gray-400 mb-4">
              Reach out to us anytime. We’d love to hear from you!
            </p>

            {/* Contact Details */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <FaPhoneAlt className="text-[#1C9BF8]" />
                <span><a href="tel:07737389202">+44 7737 389 202</a></span>
              </div>

              <div className="flex items-center space-x-3">
                <FaEnvelope className="text-[#1C9BF8]" />
                <span><a href="mailto:contact@ramonssupremeautos.com">contact@ramonssupremeautos.com</a></span>
              </div>

            {/* Google Map */}
            <div className="mt-6">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d397243.1877743138!2d0.30878815812834703!3d51.79978350890096!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b4177bb8126532d%3A0xb36ecd8efef2718e!2sRamon&#39;s%20Supreme%20Automotives!5e1!3m2!1sen!2suk!4v1739130160997!5m2!1sen!2suk"
                title="Ramon's Supreme Automotive Business Location"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg shadow-lg"
              ></iframe>
            </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
